/**
 * src/features/room/ui/TemplatePicker.tsx
 *
 * Presentational card-grid for selecting a starter room template (TMPL-01).
 * This component is the CONTENT rendered inside a ResponsiveModal — it does NOT
 * wrap a modal itself. The parent (EditorPage) owns the modal wrapper.
 *
 * UI-SPEC tokens: rounded-2xl, border, dark: variants, hover ring, motion.button,
 * min-h-[44px], aria-label, role="group".
 */

import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Droplets, UtensilsCrossed, BedDouble, DoorOpen, type LucideIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { STARTER_TEMPLATES } from '@/entities/room';
import type { StarterTemplate } from '@/entities/room';

interface TemplatePickerProps {
  onSelect: (templateId: string) => void;
  onSkip: () => void;
}

const ICON_MAP: Record<string, LucideIcon> = {
  tpl_bathroom: Droplets,
  tpl_kitchen: UtensilsCrossed,
  tpl_bedroom: BedDouble,
  tpl_hallway: DoorOpen,
};

export const TemplatePicker: FC<TemplatePickerProps> = ({ onSelect, onSkip }) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-4 p-1">
      <p className="text-sm text-slate-500 dark:text-slate-400">
        {t('templates.picker_subtitle', 'Wybierz typ pomieszczenia — dane zostaną wypełnione automatycznie')}
      </p>

      <div
        className="grid grid-cols-2 sm:grid-cols-4 gap-3"
        role="group"
        aria-label={t('templates.picker_title', 'Zacznij od szablonu')}
      >
        {STARTER_TEMPLATES.map((template: StarterTemplate) => {
          const Icon = ICON_MAP[template.id] ?? DoorOpen;
          return (
            <motion.button
              key={template.id}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => onSelect(template.id)}
              className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 flex flex-col items-center gap-3 cursor-pointer transition-all min-h-[44px] hover:border-blue-400 hover:shadow-md hover:bg-slate-50 dark:hover:bg-slate-800 focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:outline-none"
              aria-label={t(template.nameKey, template.name)}
            >
              <Icon
                size={32}
                className="text-blue-600 dark:text-blue-400"
                aria-hidden="true"
              />
              <span className="text-sm font-bold text-slate-800 dark:text-slate-100 text-center">
                {t(template.nameKey, template.name)}
              </span>
              <span className="text-xs text-slate-400 dark:text-slate-500 uppercase tracking-wide text-center">
                {t(`templates.${template.id.replace('tpl_', '')}.meta`, '')}
              </span>
            </motion.button>
          );
        })}
      </div>

      <div className="flex justify-center pt-1">
        <button
          onClick={onSkip}
          className="text-sm text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors underline underline-offset-2"
          aria-label={t('templates.skip_blank', 'Pomiń — utwórz puste pomieszczenie')}
        >
          {t('templates.skip_blank', 'Pomiń — utwórz puste pomieszczenie')}
        </button>
      </div>
    </div>
  );
};
